import { useAuth } from '@workos-inc/authkit-react';
import clsx from 'clsx';
import { Suspense, useEffect, useRef, useState } from 'react';
import z from 'zod';

import { useOrganization, useUser } from 'src/api';
import { getConfig } from 'src/application/config';
import { StoredValue } from 'src/application/storage';
import { createValidationGuard } from 'src/application/validation';
import { DocumentTitle } from 'src/components/document-title';
import { ExternalLink, Link, LinkButton } from 'src/components/link';
import { Loading } from 'src/components/loading';
import LogoKoyeb from 'src/components/logo-koyeb.svg?react';
import Logo from 'src/components/logo.svg?react';
import { OrganizationAvatar } from 'src/components/organization-avatar';
import { UpgradeDialog } from 'src/components/payment-form';
import { RequestQuotaIncreaseDialog } from 'src/components/quota-increase-request-dialog';
import { FeatureFlag } from 'src/hooks/feature-flag';
import { useLocation } from 'src/hooks/router';
import { useThemeModeOrPreferred } from 'src/hooks/theme';
import { IconChevronLeft, IconPlus } from 'src/icons';
import { createTranslate } from 'src/intl/translate';
import { CommandPaletteProvider } from 'src/modules/command-palette';
import { TrialWelcomeDialog } from 'src/modules/trial/trial-welcome-dialog';
import { inArray } from 'src/utils/arrays';

import { OrganizationProjectSwitcher } from '../organization-project-switcher';
import { OrganizationSwitcher } from '../organization-switcher';

import { AppBreadcrumbs } from './app-breadcrumbs';
import { ContextPalette } from './context-palette';
import { FeatureFlagsDialog } from './feature-flags-dialog';
import { GlobalAlert } from './global-alert';
import { HelpLinks } from './help-links';
import { Layout } from './layout';
import { Navigation } from './navigation';
import { OrganizationPlan } from './organization-plan';
import { PlatformStatus } from './platform-status';
import { UserMenu } from './user-menu';

const T = createTranslate('layouts.main');

type LayoutProps = {
  children?: React.ReactNode;
};

export function MainLayout({ children }: LayoutProps) {
  const pageContext = usePageContext();
  const authKit = useAuth();

  if (!useOrganization()) {
    return null;
  }

  return (
    <CommandPaletteProvider>
      <DocumentTitle />

      <UpgradeDialog />
      <RequestQuotaIncreaseDialog />
      <FeatureFlagsDialog />
      <TrialWelcomeDialog />
      <ContextPalette />

      {authKit.impersonator && <Impersonation email={authKit.user?.email} />}

      <Layout
        banner={<Banner />}
        header={<AppBreadcrumbs />}
        menu={<Menu />}
        menuCollapsed={<Menu collapsed />}
        main={<Main>{children}</Main>}
        context={pageContext.enabled ? <PageContext {...pageContext} /> : null}
        contextExpanded={pageContext.expanded}
      />
    </CommandPaletteProvider>
  );
}

function Banner() {
  return (
    <div className="bg-green/10 px-4 py-1.5 text-center text-green md:h-full md:whitespace-nowrap">
      <ExternalLink
        openInNewTab
        href="https://koyeb.com/blog/koyeb-is-joining-mistral-ai-to-build-the-future-of-ai-infrastructure"
        className="hover:underline"
      >
        <T id="banner" />
      </ExternalLink>
    </div>
  );
}

function Menu({ collapsed = false }: { collapsed?: boolean }) {
  const organization = useOrganization();
  const isDeactivated = inArray(organization?.status, ['DEACTIVATING', 'DEACTIVATED']);

  return (
    <div className="col min-h-full gap-4 py-4 sm:gap-6 sm:py-6">
      <Link to={isDeactivated ? '/settings' : '/'} className="mx-2 px-3">
        {collapsed && <Logo className="h-6" />}
        {!collapsed && <LogoKoyeb className="h-6" />}
      </Link>

      {collapsed && (
        <div className="col h-12 items-center justify-center">
          <OrganizationAvatar className="size-6 rounded-full" />
        </div>
      )}

      {!collapsed && (
        <div className="col px-3">
          <FeatureFlag feature="simple-projects" fallback={<OrganizationSwitcher showCreateOrganization />}>
            <OrganizationProjectSwitcher showCreateOrganization />
          </FeatureFlag>
        </div>
      )}

      <LinkButton size={2} to="/services/new" disabled={isDeactivated} className="mx-3 capitalize">
        {collapsed && (
          <div>
            <IconPlus className="size-5" />
          </div>
        )}
        {!collapsed && <T id="createService" />}
      </LinkButton>

      <Navigation collapsed={collapsed} />

      <div className="mt-auto col gap-4">
        {!collapsed && (
          <div role="menu" className="mx-4 divide-y rounded-md border bg-neutral">
            <UserMenu collapsed={collapsed} />
            <OrganizationPlan />
          </div>
        )}

        <div className="col gap-2">
          <HelpLinks collapsed={collapsed} />
          <PlatformStatus collapsed={collapsed} />
        </div>
      </div>
    </div>
  );
}

function Main({ children }: { children: React.ReactNode }) {
  return (
    <main className="px-2 py-4 sm:px-4">
      <Suspense fallback={<Loading />}>
        <GlobalAlert />
        {children}
      </Suspense>
    </main>
  );
}

function Impersonation({ email }: { email?: string }) {
  return (
    <div className="pointer-events-none fixed inset-0 z-70 border-4 border-orange">
      <div className="absolute bottom-0 left-1/2 -translate-x-1/2 rounded-t-md bg-orange px-2 pt-1 text-xs text-black">
        <T id="impersonating" values={{ email }} />
      </div>
    </div>
  );
}

const isReadyEvent = createValidationGuard(z.object({ ready: z.literal(true) }));

type PageContextProps = {
  expanded?: boolean;
  setExpanded: (expanded: boolean) => void;
};

function PageContext({ expanded, setExpanded }: PageContextProps) {
  const pageContextBaseUrl = getConfig('pageContextBaseUrl');
  const { getAccessToken } = useAuth();

  const location = useLocation();
  const theme = useThemeModeOrPreferred();

  const iFrameRef = useRef<HTMLIFrameElement>(null);
  const [ready, setReady] = useState(0);

  useEffect(() => {
    function listener(event: MessageEvent<unknown>) {
      if (event.origin === pageContextBaseUrl && isReadyEvent(event.data)) {
        setReady((ready) => ready + 1);
      }
    }

    window.addEventListener('message', listener);
    return () => window.removeEventListener('message', listener);
  }, [pageContextBaseUrl, iFrameRef]);

  useEffect(() => {
    if (pageContextBaseUrl !== undefined && ready) {
      void getAccessToken().then((token) => {
        iFrameRef.current?.contentWindow?.postMessage({ token, location }, pageContextBaseUrl);
      });
    }
  }, [getAccessToken, pageContextBaseUrl, iFrameRef, ready, location]);

  return (
    <>
      <button
        onClick={() => setExpanded(!expanded)}
        className={clsx(
          'absolute right-full hidden h-full justify-center bg-muted/50 transition-opacity sm:col',
          !expanded && 'opacity-0 hover:opacity-100',
        )}
      >
        <IconChevronLeft className={clsx('icon', expanded && '-scale-x-100')} />
      </button>

      <iframe
        ref={iFrameRef}
        src={`${pageContextBaseUrl}/context?theme=${theme}`}
        allow="clipboard-write"
        className="size-full border-l"
      />
    </>
  );
}

const pageContextExpanded = new StoredValue<boolean>('page-context-expanded');

function usePageContext() {
  const { impersonator } = useAuth();
  const user = useUser();
  const pageContextBaseUrl = getConfig('pageContextBaseUrl');

  const enabled = Boolean(
    pageContextBaseUrl !== undefined && (user?.flags.includes('ADMIN') || impersonator !== null),
  );

  const [expanded, setExpanded] = useState(pageContextExpanded.read() ?? false);

  return {
    enabled,
    expanded: enabled && expanded,
    setExpanded: (expanded: boolean) => {
      setExpanded(expanded);
      pageContextExpanded.write(expanded);
    },
  };
}
